import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl, isNull } from '@core/tools';
import { EApproveType, EProxyPayType, ESubOrderType, IDutyAcceptPackageContractViewItem, ISettlementDetailListItem, ISettlementDetailListParam, ISettlementListItem, ISettlementListParam, ISettlementOrderDetail } from '@core/main/types';
import {
  ESettlementStatus, ICrowdOrderDetailNo, IErrServiceFeeOrderItem,
  ISettlementDutyDetail, ISettlementOrderImportParams, ISettlementOrderDetailObj,
  ISettlementOrderPayInfo, ISettlementOrderPayParam, ISettlementReadOrderImportParams,
  ISettlementStatistics, IErrRefundOrderItem, IErrRefundOrder,
  IErrRefundOrderParam, IUploadDeliverablesParams,
} from '@core/main/types/settlement';

const hostUrl = getHostbaseUrl();

/**
 * 众包灵工 - 结算订单列表查询
 */
export async function querySettlements({
  params,
  module = 'zhongbao',
  term,
}: {
  params: ISettlementListParam;
  module: 'zhongbao' | 'cuohe';
  term: 'company' | 'agent' | 'saas' | 'server';
}) {
  let total = 0;
  let data = [] as ISettlementListItem[];

  const path = `${module === 'zhongbao' ? 'crowd' : 'match'}/order/page`;

  try {
    const { code, context, message } = await Http.post<{ data: ISettlementListItem[]; total: number }>(
      `${hostUrl}/pay/${path}`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: ISettlementListItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      data.forEach((item) => {
        item.showReceiptBtn = !!item.receiptUrl;
        item.showContractBtn = !!item.contractUrl && item.orderSourceType != ESubOrderType.OFFLINE_IMPORT;
        item.showPayBtn = term === 'company' && item.orderStatus === ESettlementStatus.UNPAID;
        item.showCancelBtn = term === 'company' && (item.orderStatus === ESettlementStatus.UNPAID || item.orderStatus === ESettlementStatus.CHECK_PENDING);
        item.showDeliverablesBtn = module === 'zhongbao';
      });
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 众包灵工 - 结算订单明细列表查询
 */
export async function querySettlementDetailList({
  params,
  module = 'zhongbao',
}: {
  params: ISettlementDetailListParam;
  module: 'zhongbao' | 'cuohe';
}) {
  let total = 0;
  let data = [] as ISettlementDetailListItem[];

  const path = `${module === 'zhongbao' ? 'crowd' : 'match'}/order/order-detail-page`;

  try {
    const { code, context, message } = await Http.post<{ data: ISettlementDetailListItem[]; total: number }>(
      `${hostUrl}/pay/${path}`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: ISettlementDetailListItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      data.forEach((item) => {
        item.showReceiptBtn = !!item.receiptUrl;
        item.showContractBtn = !!item.contractUrl;
        item.showSettlementBtn = module === 'zhongbao' ? !!item.crowdSubContractUrl : false;
      });
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 众包灵工 - 结算订单所有明细列表查询
 */
export async function queryAllSettlementDetailList(orderId: number, module: 'zhongbao' | 'cuohe') {
  let data = [] as ISettlementDetailListItem[];

  const path = `${module === 'zhongbao' ? 'crowd' : 'match'}/order/order-detail-list`;

  try {
    const { code, context, message } = await Http.get<ISettlementDetailListItem[]>(
      `${hostUrl}/pay/${path}`,
      { orderId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 众包灵工 - 调整子订单企业服务费
 */
export async function adjustSubOrderFee(orderDetailId: number, companyServiceAmount: number, module: 'zhongbao' | 'cuohe') {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/adjust/service-amount`,
      {
        orderDetailId,
        companyServiceAmount,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 取消结算订单
 */
export async function cancelSettlementOrder(orderId: number, module: 'zhongbao' | 'cuohe') {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/cancel-pay/${orderId}`,
      {},
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 删除结算订单
 */
export async function deleteSettlementOrders(orderIds: number[], module: 'zhongbao' | 'cuohe') {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/delete/batch`,
      { orderIds },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 结算订单列表批量导出
 */
export async function exportSettlementListOrders(params: ISettlementListParam, module: 'zhongbao' | 'cuohe') {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/export-order`,
      params,
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 众包灵工 - 结算订单明细列表批量导出
 */
export async function exportSettlementDetailList(params: ISettlementDetailListParam, module: 'zhongbao' | 'cuohe') {
  let buffer = null as Blob | null;
  let fileName = ''; // 默认文件名

  try {
    const { code, context, message, headers } = await Http.post<Blob>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/export-order-detail`,
      params,
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);

      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}

/**
 * 众包灵工 - 结算子订单详情查询
 */
export async function querySettlementDetail(orderId: number, module: 'zhongbao' | 'cuohe') {
  let data = {} as ISettlementOrderDetail;

  try {
    const { code, context, message } = await Http.get<ISettlementOrderDetail>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/get-order-info`,
      { orderId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as ISettlementOrderDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 众包灵工 - 模糊查询任务列表
 */
export async function queryDutyList(module: 'zhongbao' | 'cuohe', companyId?: number, proxyPayType?: EProxyPayType) {
  let data = [] as ISettlementDutyDetail[];

  const dutyType = module === 'cuohe' ? 1 : 0;

  try {
    const { code, context, message } = await Http.post<ISettlementDutyDetail[]>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/get-duty-list`,
      {
        dutyType,
        companyId,
        proxyPayType, 
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 获取结算支付单详细信息
 * @param orderId 订单ID
 * @returns 支付单详细信息
 */
export async function querySettlementOrderPayInfo(orderId: string, module: 'zhongbao' | 'cuohe'): Promise<ISettlementOrderPayInfo> {
  try {
    const { code, context, message } = await Http.get<ISettlementOrderPayInfo>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/get-pay-Info`,
      { orderId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      return context || {} as ISettlementOrderPayInfo;
    }

    throw new Error(message || '获取支付单信息失败');
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

export const needAuthCode = '10010';

/**
 * 提交结算订单支付
 * @param params 支付请求参数
 * @returns 支付操作结果
 */
export async function doSettlementOrderPayment(params: ISettlementOrderPayParam, module: 'zhongbao' | 'cuohe'): Promise<string> {
  try {
    const { code, message } = await Http.post<Record<string, never>>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/pay`,
      { ...params },
    );

    if (code.toString() === needAuthCode) {
      return code.toString();
    }

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '支付操作失败');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 查询指定任务的个人子订单
 */
export async function queryFindDutyPersonSettlement(dutyId: number, personIdNumber: string) {
  let data = [] as ICrowdOrderDetailNo[];

  try {
    const { code, context, message } = await Http.post<ICrowdOrderDetailNo[]>(
      `${hostUrl}/pay/crowd/order/find-duty-person/detail-order`,
      {
        dutyId,
        personIdNumber,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as ICrowdOrderDetailNo[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 众包灵工 - 查询指定任务的个人子订单
 */
export async function queryMatchFindDutyPersonSettlement(dutyId: number, personIdNumber: string) {
  let data = [] as ICrowdOrderDetailNo[];

  try {
    const { code, context, message } = await Http.post<ICrowdOrderDetailNo[]>(
      `${hostUrl}/pay/match/order/find-duty-person/detail-order`,
      {
        dutyId,
        personIdNumber,
      },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || [] as ICrowdOrderDetailNo[];
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 众包灵工 - 查询审批权限
 */
export async function checkAuditPermission(orderId: number, type: EApproveType) {
  let enabled = true;
  let taskNo = '';

  try {
    const { code, context } = await Http.get<Array<{ taskNo: string }>>(
      `${hostUrl}/iam/approve/task/list`,
      {
        refId: orderId,
        type,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      enabled = false;
    } else if (isNull(context) || context.length === 0) {
      enabled = false;
    } else {
      taskNo = context[0]?.taskNo || '';
    }
  } catch (error) {
    enabled = false;
  }

  return {
    enabled,
    taskNo,
  };
}

/**
 * 结算订单  回单下载
 */
export async function downloadReceiptsAnyType(params: any) {
  try {
    const { code, message } = await Http.post<string>(
      `${hostUrl}/pay/flex-work/receipt/batch-download`,
      params,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 查询结算统计信息
 */
export async function querySettStatistics(params: ISettlementDetailListParam, module: 'zhongbao' | 'cuohe' = 'zhongbao') {
  try {
    const { code, context, message } = await Http.post<ISettlementStatistics>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/statistics`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    return context || {} as ISettlementStatistics;
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 查询结算统计信息
 */
export async function queryPreviewSubContract(orderId: number, module: 'zhongbao' | 'cuohe' = 'zhongbao') {
  try {
    const { code, context, message } = await Http.get<IDutyAcceptPackageContractViewItem[]>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/preview-ch-sub-contract`,
      { orderId },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    return context || {} as IDutyAcceptPackageContractViewItem[];
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 -  查询是否展示结算说明
 */
export async function isShowOrderRemark() {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/side/company/isShowOrderRemark`,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    return context as any;
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 线下导入 - 读取订单excel
 */
export async function readOrderExcel(params: ISettlementReadOrderImportParams, module: 'zhongbao' | 'cuohe' = 'zhongbao') {
  let data = {} as ISettlementOrderImportParams;

  try {
    const { code, context, message } = await Http.post<ISettlementOrderImportParams>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/read-order-excel`,
      params,
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as ISettlementOrderImportParams;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 众包灵工 - 线下导入-导入订单
 */
export async function importSettlementOrder(params: ISettlementOrderImportParams, module: 'zhongbao' | 'cuohe' = 'zhongbao') {
  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/pay/${module === 'zhongbao' ? 'crowd' : 'match'}/order/import-order`,
      params,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    return context as any;
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}
/**
 * 众包灵工 - 结算订单明细详情
 */
export async function querySettlementOrderDetail(orderDetailId: number) {
  let data = {} as ISettlementOrderDetailObj;

  try {
    const { code, context, message } = await Http.get<ISettlementOrderDetailObj>(`${hostUrl}/pay/crowd/order/get-order-detail-info`, { orderDetailId });

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as ISettlementOrderDetailObj;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 众包灵工 - 结算订单明细 - 更新详情
 */
export async function updateSettlementOrderDetail(params: ISettlementOrderDetailObj) {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/pay/crowd/order/update-order-detail`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

}

/**
 * 众包灵工 - 结算订单 - 异常处理-获取服务费支付失败的订单分页列表
 */
export async function queryErrServiceFeeOrders() {
  let total = 0;
  let data = [] as IErrServiceFeeOrderItem[];

  try {
    const { code, context, message } = await Http.post<{ data: IErrServiceFeeOrderItem[]; total: number }>(`${hostUrl}/pay/crowd/order/get-err-service-fee-orders`);

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IErrServiceFeeOrderItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 众包灵工 - 结算订单 - 异常处理支付失败全部重新支付
 */
export async function retryPayErrServiceFeeOrders() {
  try {
    const { code, message } = await Http.post<void>(`${hostUrl}/pay/crowd/order/retryPay`);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 众包灵工 - 结算订单 - 异常处理-获取退款的订单分页列表【个人退汇后需要退还技术服务费】
 */
export async function queryErrRefundOrders(params: IErrRefundOrderParam) {
  let total = 0;
  let data = [] as IErrRefundOrderItem[];

  try {
    const { code, context, message } = await Http.post<{ data: IErrRefundOrderItem[]; total: number }>(`${hostUrl}/pay/crowd/order/get-refund-orders`, params);

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IErrRefundOrderItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 众包灵工 - 结算订单 - 异常处理-导出退款的订单分页列表【个人退汇后需要退还技术服务费】
 */
export async function exportErrRefundOrders(orderDetailIds: number[]) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.post<Blob>(`${hostUrl}/pay/crowd/order/export-refund-orders`, { orderDetailIds }, { responseType: 'blob' });

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 众包灵工 -  查询是否展示结算说明
 */
export async function queryCrowdErrOrder() {
  let count = 0 as number;

  try {
    const { code, context, message } = await Http.get<number>(`${hostUrl}/pay/crowd/order/count-err-service-fee-orders`);

    if (code === EAxiosResponseCode.Succeed) {
      count = context || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
  return count;
}

/**
 * 众包灵工 -  异常订单 - 退款
 */
export async function crowdOrderRefund(params: IErrRefundOrder) {
  try {
    const { code, message } = await Http.post<any>(`${hostUrl}/pay/crowd/order/refund`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 上传交付物
 */
export async function uploadDeliverables(params: IUploadDeliverablesParams) {
  try {
    const { code, message } = await Http.post<IUploadDeliverablesParams>(`${hostUrl}/pay/crowd/order/save-order-deliverable`, params);

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}